/** DEV-444 — shared handling for the backend photo content-safety guardrail. */

/** `reason` is a user-facing message only when `reasonCode` is set (older backends sent model text). */
export type RejectedPhoto = { inputIndex: number; reason: string; reasonCode?: string | null };

type RawRejected = { index: number | string; reason: string; reason_code?: string | null };

export class PhotoGuardrailError extends Error {
  kind: "rejected" | "unavailable"; // 422 vs 503
  rejectedPhotos: RejectedPhoto[];
  httpStatus: number;
  /** Set on rejections from current backends; `message` is then user-facing. */
  reasonCode: string | null;

  constructor(
    kind: "rejected" | "unavailable",
    message: string,
    rejectedPhotos: RejectedPhoto[],
    httpStatus: number,
    reasonCode: string | null = null,
  ) {
    super(message);
    this.name = "PhotoGuardrailError";
    this.kind = kind;
    this.rejectedPhotos = rejectedPhotos;
    this.httpStatus = httpStatus;
    this.reasonCode = reasonCode;
  }
}

/** Result of a successful (2xx) guarded upload: the backend body + normalised rejections. */
export type GuardrailResult<T = any> = T & { rejectedPhotos: RejectedPhoto[] };

// Backend's `index` format varies by surface ("image0", "1#levelImage||image_0", bare int, …);
// map it back to the FormData key's array position so callers get a uniform index.
function toRejectedPhotos(
  raw: RawRejected[] | undefined,
  fieldKeys: string[],
): RejectedPhoto[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((r) => {
    const key = String(r.index);
    let inputIndex = fieldKeys.indexOf(key);
    // Fallback: some surfaces return a bare integer position instead of a FormData key.
    if (inputIndex === -1 && /^\d+$/.test(key)) inputIndex = Number(key);
    return { inputIndex, reason: r.reason ?? "", reasonCode: r.reason_code ?? null };
  });
}

/**
 * DEV-443 — native variant of `resolveGuardrail`, for callers that parse their own body.
 *
 * Keys off the HTTP status *only*. Unlike `resolveGuardrail`, a `status:"error"` body is
 * left alone: the mobile endpoints reuse that shape for non-guardrail failures (permission
 * / validation), which must stay generic errors on their existing path.
 *
 * Throws `PhotoGuardrailError` on 422 rejection or 503 outage; returns normally otherwise.
 * Callers wrapping this in a legacy try/catch must re-throw `PhotoGuardrailError` so the
 * typed error isn't flattened into a string.
 *
 * @param fieldKeys FormData keys used for each photo, in submit order.
 */
export function throwIfGuardrailRejected(
  response: Response | null | undefined,
  data: any,
  fieldKeys: string[],
): void {
  if (!response) return;

  if (response.status === 422 && data?.status === "rejected") {
    throw new PhotoGuardrailError(
      "rejected",
      data?.reason ?? data?.message ?? "",
      toRejectedPhotos(data?.rejected_photos, fieldKeys),
      422,
      data?.reason_code ?? null,
    );
  }

  // Fail-closed: treat a guardrail outage as blocking, not as "nothing rejected".
  if (response.status === 503) {
    throw new PhotoGuardrailError(
      "unavailable",
      data?.message ?? "Content safety check is currently unavailable.",
      [],
      503,
    );
  }
}

/**
 * DEV-443 — normalises the partial-success case (2xx body that dropped some photos)
 * for callers that parse their own body and keep their existing return shape.
 */
export function getRejectedPhotos(data: any, fieldKeys: string[]): RejectedPhoto[] {
  return toRejectedPhotos(data?.rejected_photos, fieldKeys);
}

/**
 * Classifies a guarded upload `Response`, throwing on rejection or guardrail unavailability.
 * @param fieldKeys FormData keys used for each photo, in submit order.
 * @returns The parsed body plus a normalised `rejectedPhotos` array.
 */
export async function resolveGuardrail(
  response: Response | null | undefined,
  fieldKeys: string[],
): Promise<GuardrailResult> {
  if (!response) {
    throw new PhotoGuardrailError("unavailable", "Network error", [], 0);
  }

  let data: any;
  try {
    data = await response.json();
  } catch {
    throw new PhotoGuardrailError(
      "unavailable",
      `Unexpected response (HTTP ${response.status})`,
      [],
      response.status,
    );
  }

  // Fail-closed: treat a guardrail outage as blocking, not as "nothing rejected".
  if (response.status === 503 || data?.status === "error") {
    throw new PhotoGuardrailError(
      "unavailable",
      data?.message ?? "Content safety check is currently unavailable.",
      [],
      response.status,
    );
  }

  // Whole action rejected, as opposed to the partial-success case below (2xx + rejectedPhotos).
  if (data?.status === "rejected") {
    throw new PhotoGuardrailError(
      "rejected",
      data?.reason ?? data?.message ?? "",
      toRejectedPhotos(data?.rejected_photos, fieldKeys),
      response.status,
      data?.reason_code ?? null,
    );
  }

  return { ...data, rejectedPhotos: toRejectedPhotos(data?.rejected_photos, fieldKeys) };
}
